import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import { formatPrice, getCurrency, initializeCurrency } from './utils/currency';
import './BookFlightPage.css';

const BookFlightPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [tripType, setTripType] = useState('round-trip');
  const [formData, setFormData] = useState({
    from: '',
    to: '',
    departureDate: '',
    returnDate: '',
    passengers: {
      adults: 1,
      children: 0,
      infants: 0
    },
    class: 'Economy'
  });
  const [multiCityRoutes, setMultiCityRoutes] = useState([
    { from: '', to: '', date: '' }
  ]);
  const [currency, setCurrency] = useState(null);
  const [autoSearch, setAutoSearch] = useState(false);

  // Handle prefill data from navigation
  useEffect(() => {
    const prefillData = location.state?.prefillData;
    if (prefillData) {
      console.log('BookFlightPage: Received prefill data:', prefillData);
      
      // Set trip type
      if (prefillData.tripType) {
        setTripType(prefillData.tripType);
      }
      
      // Set form data
      setFormData(prev => ({
        ...prev,
        from: prefillData.from || prev.from,
        to: prefillData.to || prev.to,
        departureDate: prefillData.departureDate || prev.departureDate,
        returnDate: prefillData.returnDate || prev.returnDate
      }));
      
      // Set auto-search flag
      if (prefillData.from && prefillData.to && prefillData.departureDate) {
        setAutoSearch(true);
      }
    }
  }, [location.state]);

  // Initialize currency on component mount
  useEffect(() => {
    const initCurrency = async () => {
      try {
        console.log('BookFlightPage: Initializing currency...');
        const detectedCurrency = await initializeCurrency();
        setCurrency(detectedCurrency);
        console.log('BookFlightPage: Currency initialized:', {
          code: detectedCurrency.code,
          symbol: detectedCurrency.symbol,
          name: detectedCurrency.name
        });
      } catch (error) {
        console.error('BookFlightPage: Error initializing currency:', error);
        const fallbackCurrency = getCurrency();
        setCurrency(fallbackCurrency);
        console.log('BookFlightPage: Using fallback currency:', {
          code: fallbackCurrency.code,
          symbol: fallbackCurrency.symbol
        });
      }
    };
    initCurrency();

    // Listen for currency initialization from App
    const handleCurrencyInit = (event) => {
      console.log('BookFlightPage: Received currency from App:', {
        code: event.detail.code,
        symbol: event.detail.symbol,
        name: event.detail.name
      });
      setCurrency(event.detail);
    };
    window.addEventListener('currencyInitialized', handleCurrencyInit);

    return () => {
      window.removeEventListener('currencyInitialized', handleCurrencyInit);
    };
  }, []);

  const nigerianAirports = [
    { code: 'LOS', name: 'Lagos (LOS)', city: 'Lagos' },
    { code: 'ABV', name: 'Abuja (ABV)', city: 'Abuja' },
    { code: 'PHC', name: 'Port Harcourt (PHC)', city: 'Port Harcourt' },
    { code: 'KAN', name: 'Kano (KAN)', city: 'Kano' },
    { code: 'ENU', name: 'Enugu (ENU)', city: 'Enugu' },
    { code: 'BNI', name: 'Benin (BNI)', city: 'Benin' }
  ];

  const popularRoutes = [
    { from: 'Lagos', to: 'Abuja', fromCode: 'LOS', toCode: 'ABV', priceNGN: 45000, airline: '✈️' },
    { from: 'Abuja', to: 'Port Harcourt', fromCode: 'ABV', toCode: 'PHC', priceNGN: 52000, airline: '✈️' },
    { from: 'Lagos', to: 'Accra', fromCode: 'LOS', toCode: 'ACC', priceNGN: 95000, airline: '✈️' },
    { from: 'Abuja', to: 'Dubai', fromCode: 'ABV', toCode: 'DXB', priceNGN: 420000, airline: '✈️' },
    { from: 'Lagos', to: 'London', fromCode: 'LOS', toCode: 'LHR', priceNGN: 680000, airline: '✈️' },
    { from: 'Lagos', to: 'Johannesburg', fromCode: 'LOS', toCode: 'JNB', priceNGN: 580000, airline: '✈️' }
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePassengerChange = (type, value) => {
    setFormData(prev => ({
      ...prev,
      passengers: {
        ...prev.passengers,
        [type]: parseInt(value) || 0
      }
    }));
  };

  const handleAddMultiCityRoute = () => {
    setMultiCityRoutes(prev => [...prev, { from: '', to: '', date: '' }]);
  };

  const handleMultiCityChange = (index, field, value) => {
    const updated = [...multiCityRoutes];
    updated[index][field] = value;
    setMultiCityRoutes(updated);
  };

  const handleRemoveMultiCityRoute = (index) => {
    if (multiCityRoutes.length > 1) {
      setMultiCityRoutes(prev => prev.filter((_, i) => i !== index));
    }
  };

  const performSearch = () => {
    // Format data to match HomePage format
    const totalPassengers = formData.passengers.adults + formData.passengers.children + formData.passengers.infants;
    const passengersString = totalPassengers.toString();
    
    // Convert class to travelClass (lowercase for consistency)
    const travelClass = formData.class.toLowerCase();
    
    // Format search data to match HomePage structure
    const finalSearchData = {
      tripType,
      from: formData.from,
      to: formData.to,
      departureDate: formData.departureDate,
      returnDate: formData.returnDate || '',
      passengers: passengersString,
      travelClass: travelClass,
      multiCityRoutes: tripType === 'multi-city' ? multiCityRoutes : null
    };

    navigate('/results', { state: { searchData: finalSearchData } });
  };

  const handleSearch = (e) => {
    e.preventDefault();
    performSearch();
  };

  // Auto-search when prefill data is ready
  useEffect(() => {
    if (autoSearch && formData.from && formData.to && formData.departureDate) {
      // Wait a bit for form to render, then auto-search
      const timer = setTimeout(() => {
        console.log('BookFlightPage: Auto-searching with prefill data');
        performSearch();
        setAutoSearch(false);
      }, 800);
      
      return () => clearTimeout(timer);
    }
  }, [autoSearch, formData.from, formData.to, formData.departureDate, formData.passengers, formData.class, tripType, multiCityRoutes, navigate]);

  const handlePopularRouteClick = (route) => {
    setFormData(prev => ({
      ...prev,
      from: route.fromCode,
      to: route.toCode
    }));
    // Scroll to booking form
    const bookingForm = document.getElementById('booking-form');
    if (bookingForm) {
      bookingForm.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const getTotalPassengers = () => {
    return formData.passengers.adults + formData.passengers.children + formData.passengers.infants;
  };

  return (
    <div className="book-flight-page">
      <Header />
      
      {/* Hero Section */}
      <section className="booking-hero-section">
        <div className="page-container">
          <div className="booking-hero-content">
            <h1 className="booking-hero-title">Book Your Flight</h1>
            <p className="booking-hero-subtitle">
              Fast, reliable, and premium flight search for Nigeria.
            </p>
          </div>
        </div>
      </section>

      {/* Booking Form Section */}
      <section id="booking-form" className="booking-form-section">
        <div className="page-container">
          <div className="booking-form-card">
            {/* Trip Type Toggle */}
            <div className="trip-type-toggle">
              <button
                type="button"
                className={`trip-type-button ${tripType === 'one-way' ? 'active' : ''}`}
                onClick={() => setTripType('one-way')}
              >
                One-Way
              </button>
              <button
                type="button"
                className={`trip-type-button ${tripType === 'round-trip' ? 'active' : ''}`}
                onClick={() => setTripType('round-trip')}
              >
                Round-Trip
              </button>
              <button
                type="button"
                className={`trip-type-button ${tripType === 'multi-city' ? 'active' : ''}`}
                onClick={() => setTripType('multi-city')}
              >
                Multi-City
              </button>
            </div>

            <form onSubmit={handleSearch} className="booking-form">
              {/* One-Way / Round-Trip Form */}
              {(tripType === 'one-way' || tripType === 'round-trip') && (
                <div className="form-fields-grid">
                  {/* From */}
                  <div className="form-field-card">
                    <label className="field-label">
                      <span className="field-icon">📍</span>
                      From
                    </label>
                    <select
                      name="from"
                      value={formData.from}
                      onChange={handleInputChange}
                      className="form-select"
                      required
                    >
                      <option value="">Select departure airport</option>
                      <optgroup label="Nigeria">
                        {nigerianAirports.map(airport => (
                          <option key={airport.code} value={airport.code}>
                            {airport.name}
                          </option>
                        ))}
                      </optgroup>
                      <optgroup label="International">
                        <option value="LHR">London Heathrow (LHR)</option>
                        <option value="DXB">Dubai (DXB)</option>
                        <option value="JFK">New York JFK (JFK)</option>
                        <option value="ACC">Accra (ACC)</option>
                        <option value="JNB">Johannesburg (JNB)</option>
                        <option value="CDG">Paris Charles de Gaulle (CDG)</option>
                        <option value="IST">Istanbul (IST)</option>
                      </optgroup>
                    </select>
                  </div>

                  {/* To */}
                  <div className="form-field-card">
                    <label className="field-label">
                      <span className="field-icon">✈️</span>
                      To
                    </label>
                    <select
                      name="to"
                      value={formData.to}
                      onChange={handleInputChange}
                      className="form-select"
                      required
                    >
                      <option value="">Select destination airport</option>
                      <optgroup label="Nigeria">
                        {nigerianAirports.map(airport => (
                          <option key={airport.code} value={airport.code}>
                            {airport.name}
                          </option>
                        ))}
                      </optgroup>
                      <optgroup label="International">
                        <option value="LHR">London Heathrow (LHR)</option>
                        <option value="DXB">Dubai (DXB)</option>
                        <option value="JFK">New York JFK (JFK)</option>
                        <option value="ACC">Accra (ACC)</option>
                        <option value="JNB">Johannesburg (JNB)</option>
                        <option value="CDG">Paris Charles de Gaulle (CDG)</option>
                        <option value="IST">Istanbul (IST)</option>
                      </optgroup>
                    </select>
                  </div>

                  {/* Departure Date */}
                  <div className="form-field-card">
                    <label className="field-label">
                      <span className="field-icon">📅</span>
                      Departure Date
                    </label>
                    <input
                      type="date"
                      name="departureDate"
                      value={formData.departureDate}
                      onChange={handleInputChange}
                      className="form-input"
                      min={new Date().toISOString().split('T')[0]}
                      required
                    />
                  </div>

                  {/* Return Date */}
                  {tripType === 'round-trip' && (
                    <div className="form-field-card">
                      <label className="field-label">
                        <span className="field-icon">📅</span>
                        Return Date
                      </label>
                      <input
                        type="date"
                        name="returnDate"
                        value={formData.returnDate}
                        onChange={handleInputChange}
                        className="form-input"
                        min={formData.departureDate || new Date().toISOString().split('T')[0]}
                        required
                      />
                    </div>
                  )}

                  {/* Passengers */}
                  <div className="form-field-card">
                    <label className="field-label">
                      <span className="field-icon">👥</span>
                      Passengers
                    </label>
                    <div className="passengers-dropdown-wrapper">
                      <div className="passengers-display">
                        {getTotalPassengers()} {getTotalPassengers() === 1 ? 'Passenger' : 'Passengers'}
                      </div>
                      <div className="passengers-details">
                        <div className="passenger-type-row">
                          <span>Adults (12+)</span>
                          <div className="passenger-controls">
                            <button
                              type="button"
                              onClick={() => handlePassengerChange('adults', Math.max(1, formData.passengers.adults - 1))}
                              className="passenger-btn"
                            >
                              −
                            </button>
                            <span>{formData.passengers.adults}</span>
                            <button
                              type="button"
                              onClick={() => handlePassengerChange('adults', formData.passengers.adults + 1)}
                              className="passenger-btn"
                            >
                              +
                            </button>
                          </div>
                        </div>
                        <div className="passenger-type-row">
                          <span>Children (2-11)</span>
                          <div className="passenger-controls">
                            <button
                              type="button"
                              onClick={() => handlePassengerChange('children', Math.max(0, formData.passengers.children - 1))}
                              className="passenger-btn"
                            >
                              −
                            </button>
                            <span>{formData.passengers.children}</span>
                            <button
                              type="button"
                              onClick={() => handlePassengerChange('children', formData.passengers.children + 1)}
                              className="passenger-btn"
                            >
                              +
                            </button>
                          </div>
                        </div>
                        <div className="passenger-type-row">
                          <span>Infants (0-2)</span>
                          <div className="passenger-controls">
                            <button
                              type="button"
                              onClick={() => handlePassengerChange('infants', Math.max(0, formData.passengers.infants - 1))}
                              className="passenger-btn"
                            >
                              −
                            </button>
                            <span>{formData.passengers.infants}</span>
                            <button
                              type="button"
                              onClick={() => handlePassengerChange('infants', formData.passengers.infants + 1)}
                              className="passenger-btn"
                            >
                              +
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Class */}
                  <div className="form-field-card">
                    <label className="field-label">
                      <span className="field-icon">💺</span>
                      Class
                    </label>
                    <select
                      name="class"
                      value={formData.class}
                      onChange={handleInputChange}
                      className="form-select"
                      required
                    >
                      <option value="Economy">Economy</option>
                      <option value="Premium Economy">Premium Economy</option>
                      <option value="Business">Business</option>
                      <option value="First Class">First Class</option>
                    </select>
                  </div>
                </div>
              )}

              {/* Multi-City Form */}
              {tripType === 'multi-city' && (
                <div className="multi-city-form">
                  {multiCityRoutes.map((route, index) => (
                    <div key={index} className="multi-city-route">
                      <div className="multi-city-header">
                        <h3 className="route-number">Route {index + 1}</h3>
                        {multiCityRoutes.length > 1 && (
                          <button
                            type="button"
                            onClick={() => handleRemoveMultiCityRoute(index)}
                            className="remove-route-btn"
                          >
                            Remove
                          </button>
                        )}
                      </div>
                      <div className="form-fields-grid">
                        <div className="form-field-card">
                          <label className="field-label">
                            <span className="field-icon">📍</span>
                            From
                          </label>
                          <select
                            value={route.from}
                            onChange={(e) => handleMultiCityChange(index, 'from', e.target.value)}
                            className="form-select"
                            required
                          >
                            <option value="">Select airport</option>
                            {nigerianAirports.map(airport => (
                              <option key={airport.code} value={airport.code}>
                                {airport.name}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="form-field-card">
                          <label className="field-label">
                            <span className="field-icon">✈️</span>
                            To
                          </label>
                          <select
                            value={route.to}
                            onChange={(e) => handleMultiCityChange(index, 'to', e.target.value)}
                            className="form-select"
                            required
                          >
                            <option value="">Select airport</option>
                            {nigerianAirports.map(airport => (
                              <option key={airport.code} value={airport.code}>
                                {airport.name}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="form-field-card">
                          <label className="field-label">
                            <span className="field-icon">📅</span>
                            Date
                          </label>
                          <input
                            type="date"
                            value={route.date}
                            onChange={(e) => handleMultiCityChange(index, 'date', e.target.value)}
                            className="form-input"
                            min={new Date().toISOString().split('T')[0]}
                            required
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={handleAddMultiCityRoute}
                    className="add-route-button"
                  >
                    + Add Another Route
                  </button>
                </div>
              )}

              {/* Search Button */}
              <button type="submit" className="search-flights-button">
                Search Flights
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* Why Fly with SymbolFly */}
      <section className="why-fly-section">
        <div className="page-container">
          <div className="why-fly-grid">
            <div className="why-fly-card">
              <div className="why-fly-icon">💳</div>
              <h3 className="why-fly-title">Best Nigerian Payment Options</h3>
              <p className="why-fly-text">
                Paystack · Flutterwave · Bank Transfer · USSD
              </p>
            </div>
            <div className="why-fly-card">
              <div className="why-fly-icon">📧</div>
              <h3 className="why-fly-title">Instant E-Ticket Delivery</h3>
              <p className="why-fly-text">
                Receive your ticket immediately via email
              </p>
            </div>
            <div className="why-fly-card">
              <div className="why-fly-icon">💬</div>
              <h3 className="why-fly-title">Premium Customer Support</h3>
              <p className="why-fly-text">
                24/7 support team ready to assist you
              </p>
            </div>
            <div className="why-fly-card">
              <div className="why-fly-icon">⭐</div>
              <h3 className="why-fly-title">Exclusive Local & International Deals</h3>
              <p className="why-fly-text">
                Best prices for Nigerian travelers
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Popular Routes */}
      <section className="popular-routes-section">
        <div className="page-container">
          <div className="popular-routes-header">
            <h2 className="popular-routes-title">Popular Routes</h2>
            <p className="popular-routes-subtitle">
              Quick access to frequently booked destinations
            </p>
          </div>
          <div className="popular-routes-grid">
            {popularRoutes.map((route, index) => (
              <div
                key={index}
                className="popular-route-card"
                onClick={() => handlePopularRouteClick(route)}
              >
                <div className="route-content">
                  <div className="route-header">
                    <span className="route-airline">{route.airline}</span>
                    <span className="route-arrow">→</span>
                  </div>
                  <h3 className="route-title">
                    {route.from} → {route.to}
                  </h3>
                  <p className="route-codes">
                    {route.fromCode} → {route.toCode}
                  </p>
                  <p className="route-price">From {formatPrice(route.priceNGN, { decimals: 0, currency: currency || getCurrency() })}</p>
                </div>
                <div className="route-action">
                  <span className="route-action-icon">→</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BookFlightPage;








